/**
 * Indexed lookup over data.travel_costs for the result-editing evaluator.
 *
 * The table is a flat list of rows {fromLocationId, toLocationId,
 * vehicleTypeId|null, cost, duration, distance} — n² rows (self-pairs
 * included), times the number of vehicle types when costs are
 * vehicle-type-dependent. There is no existing lookup helper (consumers scan
 * linearly), and the evaluator needs O(1) leg lookups, so we build a Map once
 * per table reference and memoize on that reference.
 *
 * Two lookup modes, mirroring how the BACKEND consumes the same rows:
 *  - type-aware  — rows for the vehicle's type. In a matrix where NO row is
 *    typed, every row applies to every vehicle. In one where any row is typed,
 *    the solver reads typed rows and nothing else, so an untyped row is not a
 *    fallback: it is skipped, and a pair with no typed row is a leg the solver
 *    cannot make (it fails the run with a KeyError). This is how the solver
 *    builds its per-type cost matrices, and what feeds route_stops.travelCostTo.
 *  - type-agnostic — the FIRST row for (from,to) in table order regardless of
 *    type. This mirrors the backend's `matches.iloc[0]` lookups that feed
 *    travelDurationFromPreviousStop / travelDistanceFromPreviousStop and the
 *    excess-travel "direct time". Faithful reproduction requires copying that
 *    arbitrariness, not fixing it.
 */

const keyOf = (from, to) => `${from}|${to}`;

export function buildCostIndex(travelCosts) {
  const byType = new Map(); // "from|to|typeId" -> row
  const first = new Map(); // "from|to" -> first row in table order
  // The solver's switch, not a per-row test: one typed row anywhere in the
  // table (self-pairs count) makes the whole matrix typed.
  let anyTyped = false;
  for (const row of travelCosts || []) {
    if (row == null) continue;
    if (row.vehicleTypeId != null && row.vehicleTypeId !== '') anyTyped = true;
    if (row.fromLocationId == null || row.toLocationId == null) continue;
    const pair = keyOf(row.fromLocationId, row.toLocationId);
    const typeKey = `${pair}|${row.vehicleTypeId ?? ''}`;
    if (!byType.has(typeKey)) byType.set(typeKey, row);
    if (!first.has(pair)) first.set(pair, row);
  }
  return {
    /**
     * Row for (from,to) under the given vehicle type; undefined if the solver
     * would have no row to read.
     *
     * The untyped row is a fallback ONLY in a matrix with no typed row in it.
     * This used to fall back unconditionally, so on a half-typed matrix the
     * evaluator costed legs the solver skips — it scored a plan the solver
     * would have failed to produce. An unreachable leg is now reported as one.
     */
    typeAware(from, to, vehicleTypeId) {
      const pair = keyOf(from, to);
      if (anyTyped) {
        return vehicleTypeId != null ? byType.get(`${pair}|${vehicleTypeId}`) : undefined;
      }
      return byType.get(`${pair}|`);
    },
    /** First row for (from,to) in table order (backend iloc[0] semantics). */
    typeAgnostic(from, to) {
      return first.get(keyOf(from, to));
    },
    size: byType.size,
    /** Does the solver read this matrix as typed? */
    typed: anyTyped,
  };
}

// Memoized on the table reference: the store replaces data.travel_costs
// wholesale on every change, so reference identity is a correct cache key.
let cachedTable = null;
let cachedIndex = null;

export function getCostIndex(travelCosts) {
  if (travelCosts !== cachedTable) {
    cachedTable = travelCosts;
    cachedIndex = buildCostIndex(travelCosts);
  }
  return cachedIndex;
}
